/**
 * The back button, made to mean the same thing on every screen.
 *
 * Three things go wrong with it in an app like this one, and all three have the
 * same shape: back does something the screen in front of you cannot explain.
 *
 *   A sheet is open. Back shuts the app instead of shutting the sheet, because
 *   the sheet was never a place - it is a piece of the page that appeared.
 *
 *   A board was opened cold - from a push, from a link somebody pasted, from
 *   the home-screen icon. There is nothing behind it, so back leaves for
 *   wherever the browser was before unkoad, or leaves altogether.
 *
 *   A page below home opened another page - a board from the results, or a
 *   board made by the invite on them. Back walks down through the pages it
 *   passed on the way rather than going up out of them.
 *
 * So: a screen that is open on top of another one is a history entry, and back
 * closes it. Every screen that is not home has a floor beneath it, and back
 * lands on home rather than on nothing or on a page that bounces. Home keeps
 * the browser's own answer, because back out of home is back out of the app,
 * and that is the one place where leaving is what was meant.
 *
 * Back from a page goes up, and up from every page is home. So a page opened
 * from another page that is not home takes that page's entry rather than
 * stacking on it: results to a board to results again is still one press of
 * back from home. A form cannot replace the entry it posts from, so a page
 * below home marks its own entry as one to pass on the way down instead. The
 * link up to home is the back button: it pops rather than pushing home on top.
 *
 * Nothing here decides what a screen is. It only decides where back goes.
 */

import { takesOver } from './week.js';

/** The floor: the entry back lands on when there is nothing behind a screen. */
const FLOOR = 'floor';
/** The screen itself, sitting on the floor. */
const PAGE = 'page';
/** Something opened on top of the screen, which back closes. */
const LAYER = 'layer';
/** A page that was left for another one below home, which back goes past. */
const PASS = 'pass';

/**
 * Whether the entry behind this page is somewhere worth returning to.
 *
 * The referrer is the test: a page opened from inside unkoad has one and it is
 * ours, and a push click or a home-screen launch has none. What is behind an
 * ours is home, a floor, or a page that marked itself to be passed.
 *
 * A referrer the browser has stripped reads as no referrer, which lands on
 * home - the wrong guess in the harmless direction, since home is where the
 * page's own way out points too.
 */
export function landsSomewhereReal(referrer, origin) {
    if (!referrer) return false;

    let url;
    try {
        url = new URL(referrer);
    } catch {
        return false;
    }

    return url.origin === origin;
}

/**
 * What following a link from this page does to history: 'up' for the link to
 * home, which pops; 'across' for another page below home, which takes this
 * page's entry; '' for everything the browser keeps, which includes every link
 * on home itself.
 */
export function linkMove({ href, from, origin, home = '/' }) {
    let url;
    let here;
    try {
        url = new URL(href, from);
        here = new URL(from);
    } catch {
        return '';
    }

    if (url.origin !== origin || here.pathname === home) return '';
    // A jump within the same document is not a page.
    if (url.pathname === here.pathname && url.search === here.search && url.hash !== '') return '';
    return url.pathname === home ? 'up' : 'across';
}

/**
 * Installs the guard for this page. Returns a handle the page tells about what
 * it has open, and a teardown for tests.
 *
 * `home` is both the floor's destination and the one screen that gets no
 * floor: a guard created there listens for layers and nothing else.
 */
export function createBackGuard({ win = window, home = '/' } = {}) {
    const { history, location } = win;

    /** Whether a layer of ours is standing as a history entry right now. */
    let layered = false;
    /** How to close it, handed over with each sync. */
    let dismiss = () => {};
    /**
     * Whether the pop about to arrive is one we asked for, by taking our own
     * layer entry back off the stack. It has already been dealt with - acting
     * on it again would close a second thing nobody touched.
     */
    let unwinding = false;
    /** Settles once that pop has arrived. */
    let unwound = Promise.resolve();
    let finishUnwind = () => {};

    const marker = (state) => (state && state.unkoadBack) || '';

    /**
     * Where back lands on an entry of ours that is only a way down. Both kinds
     * carry home's URL, because the page they were laid on may not render
     * itself again: an invitation already taken up redirects to its board.
     *
     * Loaded, a floor is home already and only drops its marker. Popped to
     * within the page that laid it, it is still that page, and goes home. A
     * passed page goes on down either way.
     */
    const arrive = (state, popped) => {
        const at = marker(state);
        if (at === FLOOR) {
            if (popped || location.pathname !== home) {
                // Replaced rather than pushed: home takes the floor's own
                // place, so back out of home is back out of the app.
                location.replace(home);
            } else {
                history.replaceState(null, '');
            }
            return true;
        }
        if (at === PASS) {
            history.back();
            return true;
        }
        return false;
    };
    const onShow = (event) => {
        if (event.persisted) arrive(history.state, false);
    };

    // A page reloaded while a layer was open comes back with nothing open and
    // the layer's entry underfoot. Renaming it keeps the guard honest about
    // what is standing; the duplicate entry it leaves behind costs one extra
    // press of back, which is the whole price of not tracking layers across a
    // reload.
    if (marker(history.state) === LAYER) {
        history.replaceState({ ...history.state, unkoadBack: PAGE }, '');
    }

    // The floor, laid once. Marked on the entry already there and then stepped
    // off, so that back from the page lands on it rather than leaving.
    // A new tab opened from inside unkoad has a referrer and nothing behind it.
    if (!arrive(history.state, false)
        && location.pathname !== home
        && marker(history.state) === ''
        && (history.length < 2 || !landsSomewhereReal(win.document.referrer, location.origin))) {
        const here = location.href;
        history.replaceState({ ...history.state, unkoadBack: FLOOR }, '', home);
        history.pushState({ unkoadBack: PAGE }, '', here);
    }

    /**
     * Tells the guard what the screen has open, and how to close it.
     *
     * Called on every change rather than only on the openings: closing a sheet
     * by any of its other ways out - a tap on the scrim, Escape, choosing the
     * thing it was offering - has to take our entry back off the stack, or the
     * next press of back would spend itself closing something already closed.
     */
    function sync(open, onDismiss = dismiss) {
        dismiss = onDismiss;

        if (open && !layered) {
            layered = true;
            history.pushState({ unkoadBack: LAYER }, '', location.href);
            return;
        }
        if (!open && layered) {
            layered = false;
            unwinding = true;
            unwound = new Promise((resolve) => { finishUnwind = resolve; });
            history.back();
        }
    }

    function onPop(event) {
        if (unwinding) {
            unwinding = false;
            finishUnwind();
            return;
        }

        if (arrive(event.state, true)) return;

        if (layered) {
            layered = false;
            dismiss();
        }
    }

    function onClick(event) {
        const link = event.target?.closest?.('a[href]');
        if (!link || !takesOver(event)) return;
        if (link.hasAttribute('download')) return;
        if (link.target && link.target !== '_self') return;

        const href = link.getAttribute('href');
        const move = linkMove({ href, from: location.href, origin: location.origin, home });
        if (move === '') return;

        event.preventDefault();
        if (move === 'up') {
            // Everything below a page below home leads home, so up is back -
            // past the layer too, when one is open.
            history.go(layered ? -2 : -1);
            return;
        }
        location.replace(new URL(href, location.href).href);
    }

    function onSubmit(event) {
        if (event.defaultPrevented || location.pathname === home) return;
        const form = event.target;
        if (form.target && form.target !== '_self') return;
        // The entry is about to take home's URL, and a relative action would
        // resolve against that instead of this page.
        const action = form.getAttribute('action') || '';
        if (!action.startsWith('/') || action.startsWith('//')) return;

        history.replaceState({ ...history.state, unkoadBack: PASS }, '', home);
    }

    win.addEventListener('popstate', onPop);
    win.addEventListener('pageshow', onShow);
    win.document.addEventListener('click', onClick);
    win.document.addEventListener('submit', onSubmit);

    return {
        sync,
        /**
         * Settles once history is where the guard left it. history.back() is
         * not done when it returns, and a navigation started before its pop
         * arrives is taken back by it.
         */
        settled: () => unwound,
        stop() {
            win.removeEventListener('popstate', onPop);
            win.removeEventListener('pageshow', onShow);
            win.document.removeEventListener('click', onClick);
            win.document.removeEventListener('submit', onSubmit);
        },
    };
}
